import { define, auto, type, value } from 'type-r'
import Collection, {Record} from './base'

@define
class TextElement extends Record {
    @auto(true) show: boolean
    @auto('') abbr: string
    @auto('') text: string
    @auto('') text2: string
    @auto(false) bold: boolean
    @auto('') arg1: string
    @auto('') arg2: string
    @auto('') code: string
}

@define
class LinkElement extends TextElement {
    @auto('') link: string
}

@define
class NutritionElement extends TextElement {
    @auto(0) value: number
    @auto(false) showDaily: boolean
    @auto(0) dv: number
    @auto(false) na: boolean
    @auto('') unit: string
    @auto(0) col1: number
    @auto(0) col2: number
    @auto('') prop: string
    @auto('roundCalories') roundFn: string
    @auto('') roundFn2: string
}

@define
class LabelTextOptions extends Record {
    @auto('Nutrition Facts') nutritionFacts: string
    @auto('Daily Value*') dailyValues: string
    @auto('Serving Size:') servingSize: string
    @auto('Servings Per Container') servingsPerContainer: string
    @auto('Amount Per Serving') amountPerServing: string
    @auto('') abbrDailyValues: string
    @auto('') abbrServingsPerContainer: string
    
    @auto('Calories') calories: string
    @auto('Total Fat') totalFat: string
    @auto('Calories from Fat') fatCalories: string
    @auto('Saturated Fat') satFat: string
    @auto('<em>Trans</em> Fat') transFat: string
    @auto('Polyunsaturated Fat') polyFat: string
    @auto('Monounsaturated Fat') monoFat: string
    @auto('Cholesterol') cholesterol: string
    @auto('Sodium') sodium: string
    @auto('Total Carbohydrates') carbs: string
    @auto('Dietary Fiber') fiber: string
    @auto('Sugars') sugar: string
    @auto('Includes ') addedSugars1: string
    @auto(' Added Sugars') addedSugars2: string
    @auto('Sugar Alcohol') sugarAlcohol: string
    @auto('Protein') protein: string
    
    @auto('Vitamin D') vitaminD: string
    @auto('Calcium') calcium: string
    @auto('Iron') iron: string
    @auto('Potassium') potassium: string
    @auto('Vitamin A') vitaminA: string
    @auto('Vitamin C') vitaminC: string
    @auto('Vitamin E') vitaminE: string
    @auto('VItamin K') vitaminK: string
    @auto('Thiamin') thiamin: string
    @auto('Riboflavin') riboflavin: string
    @auto('Niacin') niacin: string
    @auto('Vitamin B6') vitaminB6: string
    @auto('Folate') folate: string
    @auto('Vitamin B12') vitaminB12: string
    @auto('Pantothenic Acid') pantothenicAcid: string
    @auto('Phosphorus') phosphorus: string
    @auto('Magnesium') magnesium: string
    @auto('Zinc') zinc: string
    @auto('Selenium') selenium: string
    @auto('Copper') copper: string
    @auto('Manganese') manganese: string
    
    @auto('-') notApplicable: string
    @auto('Percent Daily Values are based on a') percentDailyPart1: string
    @auto('calorie diet') percentDailyPart2: string
    @auto('The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes to a daily diet. 2,000 calories a day is used for general nutrition advice.') percentDaily2018VersionPart1: string
    @auto(' calories a day is used for general nutrition advice.') percentDaily2018VersionPart2: string
    @auto('Data not available') dataNotAvailable: string
    @auto('Increase the Quantity Arrow') ariaLabelIncreaseQuantityArrow: string
    @auto('Decrease the Quantity Arrow') ariaLabelDecreaseQuantityArrow: string
    @auto('Change the Quantity Textbox') ariaLabelChangeQuantityTextbox: string
    @auto('Less than') calorieDietHtmlLegacyLessThan: string
    @auto('Dietary') calorieDietHtmlLegacyDietary: string
    @auto('Caffeine') caffeine: string
}

@define
export default class NutritionLabel extends Record {
    @auto("") qtys: string
    @auto("") label: string
    @auto(280) width: number //default fixedWidth of the nutrition label
    @auto(false) allowCustomWidth: boolean //to allow custom width - usually needed for mobile sites
    @auto('auto') widthCustom: string
    @auto(false) allowNoBorder: boolean //to allow the label to have no border
    @auto(false) allowFDARounding: boolean //to enable rounding of the nutritional values based on the FDA rounding rules http://goo.gl/RMD2O
    @auto(null) userFunctionNameOnQuantityChange: string //enable triggering of user function on quantity change: global function name
    @auto(null) userFunctionOnQuantityChange: string //enable triggering of user function on quantity change: handler instance
    @auto(false) hideNotApplicableValues: boolean //when set to true, this will hide the values if they are not applicable
    @auto(false) hidePercentDailyValues: boolean //when set to true, this will hide all the percent daily values
    @auto('Brand where this item belongs to') brandName: string //the brand name of the item for this label (eg. just salad)
    @auto(false) scrollLongIngredients: boolean //to scroll the ingredients if the innerheight is > scrollHeightComparison
    @auto(100) scrollHeightComparison: number //the height in px of the ingredients div
    @auto(95) scrollHeightPixel: number
    @auto('vertical') style: string
    @auto(1) decimalPlacesForNutrition: number //this is to set how many decimal places will be shown on the nutrition values (calories, fat, protein, vitamin a, iron, etc)
    @auto(0) decimalPlacesForDailyValues: number //this is to set how many decimal places will be shown for the "% daily values*"
    @auto(1) decimalPlacesForQuantityTextbox: number //this is to set how many decimal places will be shown for the serving unit quantity textbox
    @auto(true) scrollLongItemName: boolean //to scroll the item name if the jQuery.height() is > scrollLongItemNamePixel
    @auto(36) scrollLongItemNamePixel: number
    @auto(34) scrollLongItemNamePixel2018Override: number //this is needed to fix some issues on the 2018 label as the layout of the label is very different than the legacy one
    @auto(false) useBaseValueFor2018LabelAndNotDVPercentage: boolean //this is for the 2018 version
    @auto(false) useRecipeWeight: boolean
    @auto(false) singleServing: boolean
    @auto(false) variedServings: boolean
    @auto(false) shortenFootnoteSection: boolean
    @auto(1.0) valueServingUnitQuantity: number
    @auto('') valueServingSizeUnit: string
    @auto(true) showServingUnitQuantityTextbox: boolean
    //the name of the item for this label (eg. cheese burger or mayonnaise)
    @auto(TextElement) itemName: string
    @auto(true) showServingUnitQuantity: boolean
    //allow hiding of the textbox arrows
    @auto(false) hideTextboxArrows: boolean
    //this is for the 2018 version and will only work when showServingUnitQuantity is false. see https://github.com/nutritionix/nutrition-label/issues/123
    @auto(false) showOnlyTheTextServingSize: boolean
    //these 2 settings are used internally.
    //this is just added here instead of a global variable to prevent a bug when there are multiple instances of the plugin like on the demo pages
    @auto(0) originalServingUnitQuantity: number
    //this is used to fix the computation issue on the textbox
    @auto(1) nutritionValueMultiplier: number
    //this is used for the computation of the servings per container
    @auto(1) totalContainerQuantity: number
    //default calorie intake
    @auto(2000) calorieIntake: number
    
    @type(NutritionElement).as calories: NutritionElement
    @type(NutritionElement).as fatCalories: NutritionElement
    @type(NutritionElement).as totalFat: NutritionElement
    @type(NutritionElement).as satFat: NutritionElement
    @type(NutritionElement).as transFat: NutritionElement
    @type(NutritionElement).as polyFat: NutritionElement
    @type(NutritionElement).as monoFat: NutritionElement
    @type(NutritionElement).as cholesterol: NutritionElement
    @type(NutritionElement).as sodium: NutritionElement
    @type(NutritionElement).as potassium: NutritionElement
    @type(NutritionElement).as carbs: NutritionElement
    @type(NutritionElement).as fiber: NutritionElement
    
    @type(NutritionElement).as calcium: NutritionElement
    @type(NutritionElement).as iron: NutritionElement
    @type(NutritionElement).as vitaminD: NutritionElement
    @type(NutritionElement).as sugar: NutritionElement
    @type(NutritionElement).as addedSugar: NutritionElement
    @type(NutritionElement).as sugarAlcohol: NutritionElement
    @type(NutritionElement).as energyKcal: NutritionElement
    @type(NutritionElement).as protein: NutritionElement
    @type(NutritionElement).as salt: NutritionElement
    @type(NutritionElement).as magnesium: NutritionElement
    @type(NutritionElement).as phosphorus: NutritionElement
    @type(NutritionElement).as zinc: NutritionElement
    @type(NutritionElement).as copper: NutritionElement
    @type(NutritionElement).as manganese: NutritionElement
    @type(NutritionElement).as selenium: NutritionElement
    @type(NutritionElement).as thiamin: NutritionElement
    @type(NutritionElement).as riboflavin: NutritionElement
    @type(NutritionElement).as niacin: NutritionElement
    @type(NutritionElement).as pantothenicAcid: NutritionElement
    @type(NutritionElement).as vitaminB6: NutritionElement
    @type(NutritionElement).as folate: NutritionElement
    @type(NutritionElement).as vitaminB12: NutritionElement
    @type(NutritionElement).as vitaminE: NutritionElement
    @type(NutritionElement).as vitaminK: NutritionElement
    @type(NutritionElement).as vitaminA: NutritionElement
    @type(NutritionElement).as vitaminC: NutritionElement
    @type(NutritionElement).as caffeine: NutritionElement
    
    @auto(true) showServingWeightGrams: boolean
    @auto(true) showAmountPerServing: boolean
    @auto(false) showServingsPerContainer: boolean
    @auto(true) showItemName: boolean
    @auto(false) showBrandName: boolean
    @auto(false) showCalorieDiet: boolean
    @auto(false) hide_nutrition_label: boolean
    
    @type(TextElement).as ingredientList: TextElement
    @type(TextElement).as footer: TextElement
    @type(TextElement).as header: TextElement
    @type(TextElement).as disclaimer: TextElement
    @type(TextElement).as barcode: TextElement
    @type(LinkElement).as bottomlink: LinkElement
    @auto(false) abbreviate: boolean
    //see https://github.com/nutritionix/nutrition-label/issues/93 and then https://github.com/nutritionix/nutrition-label/issues/104
    @auto(true) indentSugarAndRemoveBoldStyleFor2018Label: boolean
    @auto('INGREDIENTS:') ingredientLabel: string

    @auto(0) valueServingWeightGrams: number  //these are the default values for the nutrition info
    @auto(1) valueServingPerContainer: number

    @auto('g') unitServingWeight: string //this is for the 2018 version
    @auto('g') unitGramOrMlForThePer100Part: string //this is for the uk version

    //these text settings is so you can create nutrition labels in different languages or to simply change them to your need
    @type(LabelTextOptions).as text: LabelTextOptions
    
    //round the value to the nearest number
    roundToNearestNum(input:number, nearest:number) {
	return nearest < 0 ?
	    Math.round(input * nearest) / nearest :
	    Math.round(input / nearest) * nearest;
    }
    
    roundCalories(toRound:number, decimalPlace:number) {
	toRound = this.roundCaloriesRule(toRound);
	return (toRound > 0) ? this.roundLoDash(toRound, decimalPlace) : toRound;
    }
    
    roundFat(toRound:number, decimalPlace:number) {
	toRound = this.roundFatRule(toRound);
	return (toRound > 0) ? this.roundLoDash(toRound, decimalPlace) : toRound;
    }
    
    roundSodium(toRound:number, decimalPlace:number) {
	toRound = this.roundSodiumRule(toRound);
	return (toRound > 0) ? this.roundLoDash(toRound, decimalPlace) : toRound;
    }
    
    roundPotassium(toRound:number, decimalPlace:number) {
	toRound = this.roundPotassiumRule(toRound);
	return (toRound > 0) ? this.roundLoDash(toRound, decimalPlace) : toRound;
    }
    
    roundCholesterol(toRound:number, decimalPlace:number) {
	let normalVersion = true;
	let roundResult = this.roundCholesterolRule(toRound);
	return (roundResult === false) ? '< 5' : this.roundLoDash(roundResult, decimalPlace)
    }
    
    roundCarbFiberSugarProtein(toRound:number, decimalPlace:number) {
	let normalVersion = true;
	let roundResult = this.roundCarbFiberSugarProteinRule(toRound);
	return (roundResult === false) ? '< 1' : this.roundLoDash(roundResult, decimalPlace);
    }
    
    roundCaffeine(toRound:number, decimalPlace:number) {
	toRound = this.roundToNearestNum(toRound, 1);
	return (toRound > 0) ? this.roundLoDash(toRound, decimalPlace) : toRound;
    }
    
    //Calories and Calories from Fat rounding rule
    roundCaloriesRule(toRound:number) {
	if (toRound < 5) {
	    return 0;
	} else if (toRound <= 50) {
	    //50 cal - express to nearest 5 cal increment
	    return this.roundToNearestNum(toRound, 5);
	}
	//> 50 cal - express to nearest 10 cal increment
	return this.roundToNearestNum(toRound, 10);
    }
    
    //Total Fat, Saturated Fat, Polyunsaturated Fat and Monounsaturated Fat rounding rule
    roundFatRule(toRound:number) {
	if (toRound < 0.5) {
	    return 0;
	} else if (toRound < 5) {
	    //< 5 g - express to nearest .5g increment
	    return this.roundToNearestNum(toRound, 0.5);
	}
	//>= 5 g - express to nearest 1 g increment
	return this.roundToNearestNum(toRound, 1);
    }
    
    //Sodium rounding rule
    roundSodiumRule(toRound:number) {
	if (toRound < 5) {
	    return 0;
	} else if (toRound <= 140) {
	    //5 - 140 mg - express to nearest 5 mg increment
	    return this.roundToNearestNum(toRound, 5);
	}
	return this.roundToNearestNum(toRound, 10);
    }
    
    //Potassium rounding rule
    roundPotassiumRule(toRound:number) {
	if (toRound < 5) {
	    return 0;
	} else if (toRound <= 140) {
	    //5 - 140 mg - express to nearest 5 mg increment
	    return this.roundToNearestNum(toRound, 5);
	}
	//>= 5 g - express to nearest 10 g increment
	return this.roundToNearestNum(toRound, 10);
    }
    
    //Cholesterol rounding rule
    roundCholesterolRule(toRound:number) {
	if (toRound < 2) {
	    return 0;
	} else if (toRound <= 5) {
	    return false;
	}
	//> 5 mg - express to nearest 5 mg increment
	return this.roundToNearestNum(toRound, 5);
    }
    
    //Total Carbohydrate, Dietary Fiber, Sugar and Protein rounding rule
    roundCarbFiberSugarProteinRule(toRound:number) {
	if (toRound < 0.5) {
	    return 0;
	} else if (toRound < 1) {
	    //< 1 g - express as "Contains less than 1g" or "less than 1g"
	    return false;
	}
	//> 1 mg - express to nearest 1 g increment
	return this.roundToNearestNum(toRound, 1);
    }
    
    //Total Carbohydrate, Dietary Fiber, Sugar and Protein rounding rule
    roundVitaminsCalciumIron(toRound:number) {
	if (toRound > 0) {
	    if (toRound < 10) {
		//< 10 - round to nearest even number
		return this.roundToNearestNum(toRound, 2);
	    } else if (toRound < 50) {
		//between 10 and 50, round to the nearest 5 increment
		return this.roundToNearestNum(toRound, 5);
	    }
	    //else, round to the nearest 10 increment
	    return this.roundToNearestNum(toRound, 10);
	}
	return 0;
    }
    
    roundFor2018LabelVitaminDIron(toRound:number) {
	toRound = this.roundFor2018LabelVitaminDIronRule(toRound);
	if (toRound > 0) {
	    return this.roundLoDash(toRound, 1);
	}
	return 0;
    }
    
    roundFor2018LabelVitaminDIronRule(toRound:number) {
	//round to the nearest 0.1 increment
	return this.roundToNearestNum(toRound, 0.1);
    }
    
    roundFor2018LabelCalciumPotassium(toRound:number) {
	toRound = this.roundFor2018LabelCalciumPotassiumRule(toRound);
	if (toRound > 0) {
	    return toRound;
	}
	return 0;
    }
    
    roundFor2018LabelCalciumPotassiumRule(toRound:number) {
	//round to the nearest 10 increment
	return this.roundToNearestNum(toRound, 10);
    }
    
    roundLoDash(num:number, precision:number) {
	precision = precision === null ? 0 : ( precision >= 0 ? Math.min(precision, 292) : Math.max(precision, -292) );
	if (precision) {
	    // Shift with exponential notation to avoid floating-point issues.
	    // See [MDN](https://mdn.io/round#Examples) for more details.
	    let pair = `${num}e`.split('e');
	    const value = Math.round(parseFloat(`${pair[0]}e${+pair[1] + precision}`));

	    pair = `${value}e`.split('e');
	    return +`${pair[0]}e${+pair[1] - precision}`;
	}

	return Math.round(num);
    }
}
